import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router';

import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  type UserNotification,
} from 'hooks/useNotifications';
import { Roles, setActiveRole } from 'api/tokenStorage';
import { BellIcon } from 'ui/icons/Bell.icon';

import styles from './NotificationBell.module.scss';

export type NotificationBellAccent = 'student' | 'teacher';

type NotificationBellProps = {
  accent?: NotificationBellAccent;
};

const PROFILE_LINK: Record<NotificationBellAccent, string> = {
  teacher: '/teacher',
  student: '/profile',
};

const DROPDOWN_WIDTH = 360;
const DROPDOWN_GAP = 10;
const VIEWPORT_PADDING = 8;

const formatDate = (value: string) => {
  try {
    return new Date(value).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

export const getNotificationPath = (
  item: UserNotification,
  accent: NotificationBellAccent,
): string | null => {
  const type = item.notification_type;
  const payload = item.payload ?? {};
  const base = accent === 'teacher' ? '/teacher' : '/profile';

  if (type.startsWith('bulletin_moderation_')) {
    const bulletinId = payload.bulletin_id;
    if (bulletinId != null && Number(bulletinId) > 0) {
      return `/profile/details/task/${bulletinId}`;
    }
    return '/profile/tasks';
  }

  if (type.startsWith('moderation_')) {
    const field = payload.field as string | undefined;
    if (field === 'profile_photo') {
      return `${base}/edit`;
    }
    if (field === 'education' || field === 'profile_data') {
      return `${base}/edits`;
    }
    return `${base}/edits`;
  }

  if (type === 'passport_photo_rejected' || type === 'passport_expired') {
    return '/passport-confirm';
  }

  if (type === 'review_request') {
    // Leave-review is a student action - always open student chat (force student accent).
    const params = new URLSearchParams();
    if (payload.chat_id != null) {
      params.set('chatId', String(payload.chat_id));
    }
    if (payload.bulletin_id != null) {
      params.set('bulletinId', String(payload.bulletin_id));
    }
    if (payload.room_id != null) {
      params.set('roomId', String(payload.room_id));
    }
    if (payload.tutor_user_id != null) {
      params.set('userId', String(payload.tutor_user_id));
    }
    params.set('openReview', '1');
    params.set('as', 'student');

    if (payload.chat_id != null) {
      return `/profile/chats?${params.toString()}`;
    }

    const tutorId = payload.tutor_user_id;
    if (tutorId != null) {
      return `/profile/leave-review/${tutorId}`;
    }
    return '/profile/chats?as=student';
  }

  if (type === 'student_invitation') {
    const params = new URLSearchParams();
    if (payload.chat_id != null) {
      params.set('chatId', String(payload.chat_id));
    }
    if (payload.bulletin_id != null) {
      params.set('bulletinId', String(payload.bulletin_id));
    }
    if (payload.room_id != null) {
      params.set('roomId', String(payload.room_id));
    }
    const chatPath = accent === 'teacher' ? '/teacher/chat' : '/profile/chats';
    const query = params.toString();
    return query ? `${chatPath}?${query}` : chatPath;
  }

  if (type === 'tutor_bulletin_response') {
    const params = new URLSearchParams();
    if (payload.chat_id != null) {
      params.set('chatId', String(payload.chat_id));
    }
    if (payload.bulletin_id != null) {
      params.set('bulletinId', String(payload.bulletin_id));
    }
    if (payload.room_id != null) {
      params.set('roomId', String(payload.room_id));
    }
    if (payload.tutor_user_id != null) {
      params.set('userId', String(payload.tutor_user_id));
    }
    params.set('as', 'student');
    const query = params.toString();
    return `/profile/chats?${query}`;
  }

  if (type === 'chat_blocked') {
    const params = new URLSearchParams();
    if (payload.chat_id != null) {
      params.set('chatId', String(payload.chat_id));
    }
    if (payload.bulletin_id != null) {
      params.set('bulletinId', String(payload.bulletin_id));
    }
    if (payload.room_id != null) {
      params.set('roomId', String(payload.room_id));
    }
    const chatPath = accent === 'teacher' ? '/teacher/chat' : '/profile/chats';
    const query = params.toString();
    return query ? `${chatPath}?${query}` : chatPath;
  }

  if (type === 'bulletin_available') {
    const bulletinId = payload.bulletin_id;
    if (bulletinId != null && Number(bulletinId) > 0) {
      return `/teacher/search?focus=${bulletinId}`;
    }
    return '/teacher/search';
  }

  if (type === 'trust_score_awarded') {
    return '/teacher/trust-score';
  }

  return null;
};

export const NotificationBell: React.FC<NotificationBellProps> = ({ accent = 'teacher' }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { data } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const unread = data?.unread_count ?? 0;
  const items = data?.items ?? [];
  const unreadItems = items.filter((item) => !item.is_read);
  const readItems = items.filter((item) => item.is_read).slice(0, 5);
  const accentClass = styles[`accent-${accent}`];

  const updateDropdownPosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) {
      return;
    }

    const rect = button.getBoundingClientRect();
    const width = Math.min(DROPDOWN_WIDTH, window.innerWidth * 0.92);
    let left = rect.right - width;
    const top = rect.bottom + DROPDOWN_GAP;

    if (left < VIEWPORT_PADDING) {
      left = VIEWPORT_PADDING;
    }

    if (left + width > window.innerWidth - VIEWPORT_PADDING) {
      left = window.innerWidth - width - VIEWPORT_PADDING;
    }

    setDropdownStyle({
      top,
      left,
      width,
    });
  }, []);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (!buttonRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
        setOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    updateDropdownPosition();

    const onViewportChange = () => updateDropdownPosition();
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('scroll', onViewportChange, true);

    return () => {
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('scroll', onViewportChange, true);
    };
  }, [open, updateDropdownPosition]);

  const handleOpenItem = (item: UserNotification) => {
    if (!item.is_read) {
      markRead.mutate(item.id);
    }

    const path = getNotificationPath(item, accent);
    if (path) {
      setOpen(false);
      // Leave-review deep-links must run under student accent even from teacher header.
      if (item.notification_type === 'review_request' || item.notification_type === 'tutor_bulletin_response' || path.includes('as=student')) {
        setActiveRole(Roles.student);
      }
      navigate(path);
    }
  };

  const dropdown =
    open && typeof document !== 'undefined'
      ? createPortal(
          <>
            <button
              type='button'
              className={styles.backdrop}
              aria-label='Закрыть уведомления'
              onClick={() => setOpen(false)}
            />
            <div
              ref={dropdownRef}
              className={`${styles.dropdown} ${accentClass}`}
              style={dropdownStyle}
              role='dialog'
              aria-label='Уведомления'
            >
            <div className={styles.header}>
              <span>Уведомления</span>
              {unread > 0 && (
                <button
                  type='button'
                  className={styles.markAll}
                  onClick={() => markAllRead.mutate()}
                >
                  Прочитать все
                </button>
              )}
            </div>

            <div className={styles.list}>
              {unreadItems.length === 0 && readItems.length === 0 && (
                <div className={styles.empty}>Пока нет уведомлений</div>
              )}

              {unreadItems.map((item) => (
                <button
                  key={item.id}
                  type='button'
                  className={`${styles.item} ${styles.itemUnread}`}
                  onClick={() => handleOpenItem(item)}
                >
                  <div className={styles.itemTitle}>{item.title}</div>
                  <div className={styles.itemBody}>{item.body}</div>
                  <div className={styles.itemDate}>{formatDate(item.created_at)}</div>
                </button>
              ))}

              {readItems.length > 0 && (
                <>
                  <div className={styles.sectionLabel}>Прочитанные</div>
                  {readItems.map((item) => (
                    <button
                      key={item.id}
                      type='button'
                      className={styles.item}
                      onClick={() => handleOpenItem(item)}
                    >
                      <div className={styles.itemTitle}>{item.title}</div>
                      <div className={styles.itemBody}>{item.body}</div>
                      <div className={styles.itemDate}>{formatDate(item.created_at)}</div>
                    </button>
                  ))}
                </>
              )}
            </div>

            <Link
              to={accent === 'student' ? '/profile/notifications' : accent === 'teacher' ? '/teacher/notifications' : PROFILE_LINK[accent]}
              className={styles.footer}
              onClick={() => setOpen(false)}
            >
              {accent === 'student' || accent === 'teacher' ? 'Все уведомления' : 'Перейти в профиль'}
            </Link>
          </div>
          </>,
          document.body,
        )
      : null;

  return (
    <div className={`${styles.root} ${accentClass}`}>
      <button
        ref={buttonRef}
        type='button'
        className={styles.button}
        aria-label='Уведомления'
        onClick={() => setOpen((prev) => !prev)}
      >
        <BellIcon />
        {unread > 0 && <span className={styles.badge}>{unread > 9 ? '9+' : unread}</span>}
      </button>

      {dropdown}
    </div>
  );
};
